import styles from './Forms.module.scss'
import { ActionsDropdown, PageTitle, Table } from '@Root/components'
import { generateStyleConfig } from '@Root/pages/Home/Admin/lib'
import { API } from '@Root/API'
import { useDispatch } from 'react-redux'
import { snackbarActions, modalActions } from '@Root/store'
import { errorMessage } from '@Root/helpers'
import { useCallback, useState } from 'react'
import { Modal } from '@Root/HOCs'
import { CreateNew } from './CreateNew'

export const Forms = () => {
  const dispatch = useDispatch()
  const styleConfig = generateStyleConfig(true, true, true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [shouldBeUpdated, setShouldBeUpdated] = useState(false)

  const triggerUpdate = useCallback(() => {
    setShouldBeUpdated(false)
    setTimeout(() => setShouldBeUpdated(true), 0)
  }, [])

  const columns = [
    {
      name: 'ID',
      field: 'id',
      searchInputType: 'textInput',
      cellType: 'link',
      cellLinkField: 'id_link',
    },
    {
      name: 'Cognito Form ID',
      field: 'cognito_form_id',
      searchInputType: 'textInput',
      cellType: 'text',
    },
    {
      name: 'Form Name',
      field: 'form_name',
      searchInputType: 'textInput',
      cellType: 'text',
    },
    {
      name: 'Form Type',
      field: 'form_type_label',
      searchInputType: 'textInput',
      cellType: 'text',
    },
    {
      name: 'Institution',
      field: 'institution_name',
      searchInputType: 'textInput',
      cellType: 'text',
    },
    {
      name: 'Created At',
      field: 'created_at',
      searchInputType: 'datePicker',
      cellType: 'text',
    },
    {
      name: 'Updated At',
      field: 'updated_at',
      searchInputType: 'datePicker',
      cellType: 'text',
    },
    {
      name: 'Emails Count',
      field: 'emails_count',
      searchInputType: 'textInput',
      cellType: 'text',
    },
    {
      name: 'Field Mappings Count',
      field: 'field_mappings_count',
      searchInputType: 'textInput',
      cellType: 'text',
    },
    {
      name: 'Mapping',
      field: 'mappings',
      searchInputType: 'textInput',
      cellType: 'link',
      cellLinkField: 'mappings_link',
    },
    {
      name: 'Emails',
      field: 'emails',
      searchInputType: 'textInput',
      cellType: 'link',
      cellLinkField: 'emails_link',
    },
    {
      name: 'Delete',
      field: 'delete_action',
      searchInputType: 'textInput',
      cellType: 'link',
    },
  ]

  const fetchDataHandler = async params => {
    try {
      const response = await API.cognitoForms.getForms(params)
      const { data, meta, columns: responseColumns } = response.data

      const processedData = (data || []).map(row => ({
        ...row,
        mappings: row.id,
        mappings_link: `/home/forms/${row.id}/mappings`,
        emails: 'Email Template',
        emails_link: `/home/forms/${row.id}/emails`,
        delete_action: 'Delete',
        permissions: {
          ...row.permissions,
          mappings: row.permissions?.mappings || { edit: true },
          emails: row.permissions?.emails || { edit: true },
        },
      }))

      return {
        data: {
          data: processedData,
          meta,
          columns: responseColumns || columns,
        },
      }
    } catch (error) {
      console.log(error)
    }
  }

  const onError = error => {
    dispatch(snackbarActions.setSnackbar({ text: errorMessage(error), isError: true }))
  }

  const onSuccess = message => {
    dispatch(snackbarActions.setSuccessNotification({ text: message }))
  }

  const handleDelete = useCallback(
    row => {
      new Promise((resolve, reject) => {
        dispatch(
          modalActions.showModal('ConfirmationModal', {
            text: `Are you sure you want to delete "${row.form_name}"?`,
            clickRejectButtonHandler: reject,
            clickResolveButtonHandler: resolve,
          })
        )
      })
        .then(async () => {
          try {
            await API.cognitoForms.deleteForm(row.id)
            triggerUpdate()
            onSuccess(`Form "${row.form_name}" has been deleted successfully`)
          } catch (error) {
            onError(error)
          }
        })
        .finally(() => dispatch(modalActions.hideModal()))
    },
    [dispatch]
  )

  const actions = [
    {
      name: 'Create New Form',
      handler: () => {
        setIsModalOpen(true)
      },
    },
  ]

  const onChooseAction = actionName => {
    const item = [...actions].filter(el => el.name === actionName)
    item[0]?.handler()
  }

  return (
    <div className={styles.wrapper}>
      <PageTitle title={'Cognito Forms'} style={styleConfig.pageTitle} />

      <div className={styles.actionsDropdownWrapper}>
        <ActionsDropdown actions={actions} clickHandler={onChooseAction} />
      </div>
      <Table
        name='cognito-forms'
        columns={columns}
        fetchDataHandler={fetchDataHandler}
        fetchColumnOptions={() => API.getColumnOptions('cognito-forms')}
        fetchSaveColumnOptions={options => API.saveColumnOptions('cognito-forms', options)}
        hasSorting={false}
        hasFilters={false}
        errorHandler={onError}
        successHandler={onSuccess}
        shouldBeUpdated={shouldBeUpdated}
        clickLinkHandlers={{ delete_action: handleDelete }}
        styleConfig={{
          datePeriodDropdownStyle: {
            display: 'none',
          },
        }}
      />
      {isModalOpen && (
        <Modal>
          <CreateNew onClose={() => setIsModalOpen(false)} shouldBeUpdated={triggerUpdate} />
        </Modal>
      )}
    </div>
  )
}
